// アプリケーションのURLにしたがって設定のかたまりを切り替える
// 外部への呼び出しは持たない
import type { BrowserAuthOptions } from "@azure/msal-browser";
import { useRuntimeConfig, useAppConfig, useState } from "#imports";
import type { CirclemsConfig, CirclemsAppEnv, CirclemsPublicRuntimeConfig } from "~/types/circlemsConfig";

const selectCirclemsConfigByAppEnv = (appEnv: string): CirclemsConfig => {
  const configDict = useAppConfig().circlemsConfigDict;
  switch (appEnv) {
    case "development":
      return configDict.dev;
    case "st":
    case "st-staging": // ST環境のAPIステージングは専用の設定にしない
      return configDict.stIntwcAPI;
    case "pro-staging":
      return configDict.proStgIntwcAPI;
    case "pro":
    default:
      return configDict.proIntwcAPI;
  }
};

const selectBrowserAuthOptionsByAppEnv = (appEnv: string): BrowserAuthOptions => {
  const configDict = useAppConfig().browserAuthOptionsDict;
  switch (appEnv) {
    case "development":
      return configDict.dev;
    case "st":
    case "st-staging":
      return configDict.st;
    case "pro-staging":
    case "pro":
    default:
      return configDict.pro;
  }
};

export type CirclemsSites = "circleMs" | "portal" | "auth2" | "docs" | "webCatalog" | "comiket" | "v4g"

// runtimeConfig の api, public の値を引継ぎつつ環境依存の appConfig の値を追加する
// ここには設定がない場合に 空文字 を既定値として返す処理を含む
export default function () {
  /**
   * runtimeConfig で環境が設定されていなければ URLホスト名 から appEnv を決定し その他の config を決定する
   * ここを非同期にするのはアプリケーション初期化時の挙動が不定になってしまうため 非同期にしない
   * @param hostname
   */
  const initialize = () => {
    // 初期化処理
    const requestURL = useRequestURL();
    const hostname = requestURL.hostname;
    const runtimeConfig = useRuntimeConfig();

    // このタイミングで appEnv に値が設定されていなければ 環境を判定する
    // もしhostname 以外の方法で解決したいのであれば index.html の先頭でサーバーからグローバル関数を含む JavaScript を読ませる
    const appEnv: CirclemsAppEnv = runtimeConfig.public.appEnv
      ? (runtimeConfig.public.appEnv as CirclemsAppEnv) // TODO(as-cast): 強制型キャスト。安全な型付けへ置換を検討
      : hostname === "localhost"
        ? "development"
        : hostname === "localhost.st.circle.ms"
          ? "development"
          : (hostname?.indexOf(".st.circle.ms") ?? 0) > 0
              ? "st"
              : (hostname?.indexOf("-staging.circle.ms") ?? 0) > 0
                  ? "pro-staging"
                  : "pro";
    // console.log("appEnv:", appEnv)

    return {
      origin: requestURL.origin,
      appEnv: appEnv,
      eventId: Number(runtimeConfig.public.eventId),
      logLevel: runtimeConfig.public.logLevel,
      circlemsConfig: selectCirclemsConfigByAppEnv(appEnv),
      browserAuthOptions: selectBrowserAuthOptionsByAppEnv(appEnv),
    };
  };

  // サーバ固定情報なので複数回生成する必要はない。
  const state = useState<CirclemsPublicRuntimeConfig>("circlemsPublicRuntimeConfig", () =>
    initialize() ?? {
      origin: undefined,
      appEnv: "",
      eventId: 0,
      circlemsConfig: selectCirclemsConfigByAppEnv(""),
      browserAuthOptions: selectBrowserAuthOptionsByAppEnv(""),
    });

  const getBaseUrl = (domain: CirclemsSites | undefined): string => {
    // ニュース用の markdown 表記において
    // 例えば次のように domain 属性に遷移先を指定することにより baseUrl を決定している。この時の baseUrl を返す
    // Account can be closed through [the account closure page (JP text)](/Account/WithDraw1){:target="_blank" :domain="auth2" :useEnv="true"}.
    const config = state.value.circlemsConfig;
    switch (domain) {
      case "circleMs":
        return config.CIRCLE_MS_BASE_URL || "";
      case "portal":
        return config.CIRCLE_MS_PORTAL_BASE_URL || "";
      case "auth2":
        return config.CIRCLE_MS_AUTH2_BASE_URL || "";
      case "docs":
        return config.CIRCLE_MS_DOCS_BASE_URL || "";
      case "webCatalog":
        return config.CIRCLE_MS_WEB_CATALOG_BASE_URL || "";
      case "comiket":
        return config.COMIKET_URI_BASE_URL || "";
      case "v4g":
        return config.V4G_POP_JS_URL || "";
      default:
        return "";
    }
  };

  return {
    get: (): CirclemsPublicRuntimeConfig => state.value,
    getBaseUrl,
    isDevelopment: () => state.value.appEnv === "development"
  };
}
